import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import yellow from '@material-ui/core/colors/yellow';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { AdminBox } from 'admin/useAdminBoxes';
import { Prefecture, PrefKey } from 'admin/usePrefectures';
import { Coords, GeocodeAddressQueryVariables } from 'generated/graphql';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { queryCache } from 'react-query';
import { isPlateNumberLink } from '../admin/AdminTable';
import AdminBoxMap from './AdminBoxMap';
import AdminBoxMarker from './AdminBoxMarker';
import useGeocodeAddress, {
  geocodeKey,
  getGeocodeVars,
} from './useGeocodeAddress';

export type FormData = {
  id: number | undefined;
  name: string;
  imei: string;
  plateNumber: string | null;
  postcode: string;
  prefecture: Prefecture;
  address: string;
  lat: number;
  lng: number;
};

const useStyles = makeStyles((theme: Theme) => ({
  divider: { marginTop: theme.spacing(3) },
  searchProgress: {
    color: yellow[500],
    position: 'absolute',
    top: -3,
    left: -3,
    zIndex: 1,
  },
  idInput: { display: 'none' },
}));

interface Props {
  onSubmit: (data: FormData) => void;
  box?: AdminBox;
}

const initialPrefField = { name: '', value: '' };

const UpsertBoxForm = ({ onSubmit, box }: Props) => {
  const classes = useStyles();

  const prefectures = queryCache.getQueryData<Prefecture[]>(PrefKey);

  const {
    control,
    register,
    handleSubmit,
    watch,
    errors,
    setValue,
    getValues,
  } = useForm<FormData>({
    defaultValues: {
      id: box?.id,
      name: box?.name,
      plateNumber: box?.plateNumber,
      address: box?.address ?? '',
      imei: box?.imei,
      postcode: box?.postcode ?? '',
      prefecture: useMemo(
        () => prefectures?.find((p) => p.value === box?.prefecture),
        [box, prefectures]
      ),
      lat: box?.coords.lat(),
      lng: box?.coords.lng(),
    },
  });

  const [vars, setVars] = useState<GeocodeAddressQueryVariables>({
    address: '',
  });

  const { refetch, isFetching } = useGeocodeAddress(vars);

  useEffect(() => {
    const getCoords = async (vars: GeocodeAddressQueryVariables) => {
      const coords =
        queryCache.getQueryData<Coords>([geocodeKey, vars]) ||
        (await refetch());
      if (coords?.lat && coords?.lng) {
        setValue('lat', coords.lat, { shouldDirty: true });
        setValue('lng', coords.lng, { shouldDirty: true });
      }
    };

    if (vars.address) getCoords(vars);
  }, [vars, refetch, setValue]);

  const onSearchClick = async () => {
    const vars = getGeocodeVars(getValues());
    setVars(vars);
  };

  const searchItems = watch(['prefecture', 'address', 'postcode']);
  const isSearchDisabled =
    !searchItems.address &&
    !searchItems.postcode &&
    !searchItems.prefecture?.value;

  return (
    <Box
      display="grid"
      gridTemplateRows="auto 1fr"
      gridGap="20px"
      height="100%"
    >
      <form
        id="addBoxForm"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        <Box
          display="grid"
          gridTemplateColumns={isPlateNumberLink ? '1fr 1fr 0.5fr' : '1fr 1fr'}
          gridGap="50px"
        >
          <TextField
            margin="dense"
            type="number"
            name="id"
            label="ID"
            inputRef={register}
            className={classes.idInput}
          />
          <TextField
            autoFocus
            margin="dense"
            required
            name="name"
            label="名前"
            fullWidth
            title="名前を入力してください"
            inputRef={register({ required: true })}
          />
          <TextField
            margin="dense"
            required
            name="imei"
            label="IMEI"
            title="IMEIを入力してください"
            fullWidth
            inputRef={register({
              required: true,
              minLength: {
                value: 15,
                message: '15文字のIMEIを入力してください',
              },
              maxLength: {
                value: 15,
                message: '15文字のIMEIを入力してください',
              },
            })}
            error={!!errors.imei}
            helperText={errors.imei?.message}
            onChange={(ev) => {
              ev.target.value = ev.target.value.trim();
            }}
            onBlur={(ev) => {
              ev.target.value = ev.target.value.trim();
            }}
          />
          {isPlateNumberLink ? (
            <TextField
              margin="dense"
              name="plateNumber"
              label="車番"
              title="車番"
              fullWidth
              inputRef={register()}
            />
          ) : null}
        </Box>

        <Divider className={classes.divider} />

        <Box display="grid" gridTemplateColumns="140px 120px" gridGap="50px">
          <TextField
            margin="dense"
            name="postcode"
            label="郵便番号"
            title="郵便番号"
            fullWidth
            inputRef={register({
              pattern: {
                value: /^[0-9]{3}-[0-9]{4}$|^[0-9]{7}$/,
                message: '郵便番号が正しくない。例：123-1234か1231234',
              },
            })}
            helperText={errors.postcode?.message}
            error={!!errors.postcode}
            onChange={(ev) => {
              ev.target.value = ev.target.value.trim().replace(/[^0-9-]/, '');
            }}
          />
          <Controller
            control={control}
            name="prefecture"
            defaultValue={initialPrefField}
            rules={{ required: true }}
            render={({ onChange, onBlur, value, name }) => (
              <Autocomplete
                fullWidth
                onChange={(e, option) => onChange(option)}
                onBlur={onBlur}
                value={value}
                disableClearable
                options={
                  prefectures
                    ? [initialPrefField, ...prefectures]
                    : [initialPrefField]
                }
                getOptionSelected={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="都道府県"
                    title="都道府県"
                    margin="dense"
                  />
                )}
              />
            )}
          />
        </Box>
        <Box
          display="grid"
          gridTemplateColumns="1fr auto"
          gridColumnGap="10px"
          alignItems="center"
        >
          <TextField
            margin="dense"
            name="address"
            label="住所"
            title="住所"
            inputRef={register}
            onBlur={(ev) => {
              ev.target.value = ev.target.value.trim();
            }}
            onChange={(ev) => {
              ev.target.value = ev.target.value.trimStart();
            }}
          />
          <Box position="relative">
            <IconButton onClick={onSearchClick} disabled={isSearchDisabled}>
              <SearchIcon />
            </IconButton>
            {isFetching && (
              <CircularProgress size={48} className={classes.searchProgress} />
            )}
          </Box>
        </Box>
        <Box
          display="grid"
          gridTemplateColumns="1fr 1fr"
          gridColumnGap="10px"
          alignItems="center"
        >
          <Controller
            control={control}
            name="lat"
            defaultValue=""
            rules={{ required: true, min: -90, max: 90 }}
            render={({ name, onBlur, onChange, value }) => (
              <TextField
                required
                type="number"
                inputProps={{ step: 'any', min: -90, max: 90 }}
                margin="dense"
                label="緯度"
                title="緯度を入力してください"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onChange({
                    ...e,
                    target: { ...e.target, value: +e.target.value },
                  });
                }}
                onBlur={onBlur}
                name={name}
                value={value}
              />
            )}
          />
          <Controller
            control={control}
            name="lng"
            rules={{ required: true, min: -180, max: 180 }}
            defaultValue=""
            render={({ name, onBlur, onChange, value }) => (
              <TextField
                required
                inputProps={{ step: 'any', min: -180, max: 180 }}
                margin="dense"
                label="経度"
                title="経度を入力してください"
                type="number"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onChange({
                    ...e,
                    target: { ...e.target, value: +e.target.value },
                  });
                }}
                onBlur={onBlur}
                name={name}
                value={value}
              />
            )}
          />
        </Box>
      </form>
      <AdminBoxMap>
        <AdminBoxMarker control={control} />
      </AdminBoxMap>
    </Box>
  );
};

export default UpsertBoxForm;
